import type { CommonStylesOptions } from '../types.d';

export const getPidlSecureFieldStyles = ({
  palette,
}: CommonStylesOptions) => ({
  global: {
    '@global': {
      'input[type=text].pidlddc-secure-property': {
        width: '100%',
        height: 32,
        margin: 0,
        padding: '3px 15px',
        border: '1px solid #bbb',
        backgroundColor: palette.white || '#fff',
        boxSizing: 'border-box',
        WebkitAppearance: 'none',
        appearance: 'none',
        fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
        fontSize: 14,
        lineHeight: '1.45833333',
        color: '#333',
      },
      'input[type=text].pidlddc-errorstroke, input[type=text].pidlddc-errorstroke:focus': {
        border: '1px solid #A80000',
        backgroundColor: '#fff',
        color: '#323130',
      },
      'iframe.pidlddc-secure-property': {
        width: '100%',
        height: 32,
        verticalAlign: 'middle',
      },
      'iframe#pidlddc-secure-input-cvvToken': {
        width: '50%',
        height: 32,
        marginRight: 16,
        verticalAlign: 'middle',
      },
    },
  },
  common: {
    '& .pidlddc-div-secureproperty': {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      '& > .pidlddc-label, & > .pidlddc-error': {
        flex: '0 0 100%',
      },
      '& > .pidlddc-image, & > .pidlddc-help-imageLink': {
        float: 'none',
        marginLeft: 5,
        height: 25,
        width: 'auto',
      },
    },
    '& #pidlddc-div-expiryGroup_cvvGroup': {
      marginBottom: 0,
    },
    '& .pidlddc-input-expiryDate': {
      width: '100%',
      height: 32,
    },
  },
});

export const getScaChallengeStyles = ({
  palette,
  variant = 'legacy',
}: CommonStylesOptions & { variant?: 'legacy' | 'paas' }) => {
  const pidlSecureFieldStyles = getPidlSecureFieldStyles({ palette });
  const secureFieldGlobals = pidlSecureFieldStyles.global['@global'];

  return {
    global: {
      '@global': {
        'input[type=text].pidlddc-errorstroke, input[type=text].pidlddc-errorstroke:focus': secureFieldGlobals['input[type=text].pidlddc-errorstroke, input[type=text].pidlddc-errorstroke:focus'],
        'input[type=text].pidlddc-secure-property': {
          position: 'relative',
          float: 'left',
          width: '100%',
          minWidth: variant === 'paas' ? '120px' : '96px',
          ...(variant === 'paas' && { marginLeft: '15px' }),
          minHeight: 1,
          height: 32,
          padding: '6px 8px',
          fontSize: 12,
          lineHeight: '1.45833333',
          color: '#333',
          verticalAlign: 'middle',
          backgroundColor: palette.white || '#fff',
          backgroundImage: 'none',
          border: '1px solid #bbb',
          boxSizing: 'border-box',
        },
        'input[type=text].pidlddc-secure-property:hover': {
          borderColor: '#323130 !important',
        },
        'input[type=text].pidlddc-secure-property:focus': {
          borderColor: '#0078D4 !important',
          outline: 'none',
        },
        '.sca-challenger-dialog iframe.pidlddc-secure-property, .paas-challenger-dialog iframe.pidlddc-secure-property': {
          width: '100%',
          minWidth: variant === 'paas' ? '120px' : '96px',
          height: 32,
          verticalAlign: 'middle',
        },
        '.sca-challenger-dialog iframe.pidlddc-float-left, .paas-challenger-dialog iframe.pidlddc-float-left': {
          float: 'left',
        },
        '.sca-challenger-dialog iframe#pidlddc-secure-input-cvvToken.pidlddc-col-4': {
          width: '16%',
          height: 32,
          marginLeft: 7.5,
          marginRight: 4,
          verticalAlign: 'middle',
        },
        '.paas-challenger-dialog iframe#pidlddc-secure-input-cvvToken': {
          width: '50%',
          height: 32,
          marginRight: 16,
          verticalAlign: 'middle',
        },
        '.sca-challenger-dialog iframe#pidlddc-secure-input-cvvToken': {
          width: '16%',
          height: 32,
          verticalAlign: 'middle',
        },
      },
    },
  };
};

export const getFluentPidlSecureFieldStyles = ({
  palette,
}: CommonStylesOptions) => {
  const pidlSecureFieldStyles = getPidlSecureFieldStyles({ palette });
  const securePropertyInputStyle = pidlSecureFieldStyles.global['@global']['input[type=text].pidlddc-secure-property'];

  return ({
    global: {
      '@global': {
        ...pidlSecureFieldStyles.global['@global'],
        'input[type=text].pidlddc-secure-property': {
          ...securePropertyInputStyle,
          padding: '6px 8px',
          border: '1px solid #939393',
          borderRadius: 2,
          boxShadow: 'none',
          fontFamily: '"Segoe UI","Segoe UI Web (West European)","Segoe UI",Roboto,"Helvetica Neue",sans-serif',
          lineHeight: 1.5,
          color: '#323130',
        },
        'input[type=text].pidlddc-secure-property:hover': {
          borderColor: '#323130 !important',
        },
        'input[type=text].pidlddc-secure-property:focus': {
          borderColor: '#0078D4 !important',
          outline: 'none',
        },
      },
    },
    common: {
      ...pidlSecureFieldStyles.common,
      '& .pidlddc-div-secureproperty': {
        ...pidlSecureFieldStyles.common['& .pidlddc-div-secureproperty'],
        maxWidth: 464,
      },
    },
  });
};

export const getCommonPiFormStyles = ({
  palette,
}: CommonStylesOptions) => {
  const pidlSecureFieldStyles = getPidlSecureFieldStyles({ palette });

  return ({
    global: {
      '@global': {
        '.sca-challenger-dialog': {
          '& .modal-content': {
            minWidth: 650,
            '@media (max-width:600px)': {
              minWidth: 400,
            },

            '& .pidlddc-iFrame': {
              height: '490px !important',
              '@media (max-width:600px)': {
                width: '355px !important',
                height: '530px !important',
              },
            },
          },
        },

        /*
       * Secure field styles for the PXEnableSecureField* flights.
       *
       * PidlSDK walks document.styleSheets, finds rules whose selector contains
       * ".pidlddc-secure-property" or ".pidlddc-errorstroke", strips the .pidlddc-secure-property
       * token (errorstroke is shipped verbatim), and re-injects the result into the iframe
       * document via postMessage. These rules must be @global so JSS scope prefixes don't
       * defeat that extraction.
       *
       * The input look here mirrors common's own .pidlddc-input-property rule below so the
       * secure card-number / CVV inputs visually match the cardholder name input. Font is
       * set explicitly because the secure-field iframe is a separate document and doesn't
       * inherit from the parent page body. Flows that want a different look (e.g. Fluent v9
       * on pmax-payment) can override with !important via their own @global block.
       */
        ...pidlSecureFieldStyles.global['@global'],
      },
    },
    sDisplayMode: {
      '& .pi-type-input': {
        marginRight: 8,
      },
      '& .country-input': {
        marginRight: 8,
      },
      '& .payment-type-input': {
        marginRight: 8,
      },
      '& .addFundsForm': {
        marginRight: 8,
      },
      '@global': {
        '.sca-challenger-dialog': {
          '& .modal-content': {
            minWidth: 400,

            '& .pidlddc-iFrame': {
              width: '355px !important',
              height: '530px !important',
            },
          },
        },
      },
    },
    common: {
      ...pidlSecureFieldStyles.common,
      '& .col-xs-6': {
        width: '100%',
      },
      '& .pi-type-input': {
        marginRight: 22,
      },
      '& .country-input': {
        marginRight: 22,
      },
      '& .payment-type-input': {
        marginRight: 22,
      },
      '& .addFundsForm': {
        marginRight: 22,
      },
      '& .pi-type-selector-container': {
        '& > .form-group > .col-xs-6.form-control-container': {
          paddingRight: 15,
        },
      },
      '& .form-control': {
        height: 32,
        fontSize: 14,
        padding: '3px 15px',
      },
      '& .pidlddc-error': {
        color: '#A80000',
        position: 'relative',
        float: 'left',
        width: '100%',
        minHeight: 1,
        margin: 0,
        padding: '4px 0 0 5px',
        fontSize: 12,
      },
      '& .pidlddc-input-property': {
        height: 25,
        fontSize: 14,
        lineHeight: '1.45833333',
        color: '#333',
        verticalAlign: 'middle',
        backgroundColor: palette.white,
        backgroundImage: 'none',
        border: '1px solid #bbb',
        position: 'relative',
        float: 'left',
        width: '50%',
        minHeight: 1,
        padding: '3px 15px',
        '&:focus': {
          outline: `1px solid ${palette.themePrimary}`,
          outlineOffset: '-1px',
        },
      },
      '& .adder-pidl-form-container': {
        paddingRight: 0,
      },
      '& .pidlddc-div': {
        marginBottom: 0,
      },
      '& .pidlddc-div-logo': {
        display: 'none',
      },
      '& .piid-selector-container': {
        '& .form-group': {
          marginRight: '-7px',
        },
      },
      '& .pidlddc-input-cardNumber': {
        height: 32,
        width: '100%',
        backgroundRepeat: 'no-repeat',
        backgroundPositionX: 'right',
        border: '1px solid #bbbbbb',
      },
      '& .pidlddc-input-cardNumberAmex': {
        height: 32,
        width: '100%',
        backgroundRepeat: 'no-repeat',
        backgroundPositionX: 'right',
        border: '1px solid #bbbbbb',
      },
      '& .pidlddc-div .pidlddc-div-group .pidlddc-group-inline': {
        marginTop: 10,
      },
      '& #pidlddc-label-pidlddc-div-expiryGroup': {
        marginTop: 37,
      },
      '& .pidlddc-div-cvv': {
        marginTop: 5,
      },
      '& .pidlddc-input-cvvToken': {
        width: '48%',
        height: 32,
      },
      '& .pidlddc-help-imageLink': {
        marginLeft: 16,
        marginTop: 5,
      },

      '& .pidlddc-div-cardholderName': {
        marginTop: 12,
      },
      '& .pidlddc-input-accountHolderName': {
        width: '100%',
        height: 32,
      },
      '& .pidlddc-input-prefillAddressDropdown': {
        width: '100%',
        height: 32,
      },
      '& .pidlddc-input-addressLine1': {
        width: '100%',
        height: 32,
        marginTop: 12,
      },
      '& .pidlddc-input-addressLine2 ': {
        width: '100%',
        height: 32,
        marginTop: 12,
      },
      '& .pidlddc-input-addressCity': {
        width: '100%',
        height: 32,
        marginTop: 12,
      },
      '& .pidlddc-input-addressState': {
        width: '100%',
        height: 32,
        marginTop: 12,
      },
      '& .pidlddc-input-addressProvince': {
        width: '100%',
        height: 32,
        marginTop: 12,
      },
      '& .pidlddc-input-addressCounty': {
        width: '100%',
        height: 32,
        marginTop: 12,
      },
      '& .pidlddc-input-addressPostalCode': {
        width: 210,
        height: 32,
        marginTop: 12,
        marginBottom: 16,
        '& +.pidlddc-error': {
          margin: '-16px 0 12px 0',
        },
      },
      '& .pidlddc-address-group': {
        marginTop: 37,
        display: 'block',
      },
      '& .pidlddc-div-cardNumber': {
        marginBottom: 12,
      },
      '& .pidlddc-inline-element-0': {
        display: 'inline',
      },
      '& .pidlddc-inline-element-1': {
        display: 'inline',
      },
      '& .pidlddc-group-inline .pidlddc-div-expiryMonth': {
        paddingRight: 0,
        width: '48%',
        display: 'inline-block',
        verticalAlign: 'top',
      },
      '& .pidlddc-input-expiryMonth': {
        height: 32,
        width: '100%',
        border: '1px solid #bbbbbb',
      },
      '& .pidlddc-group-inline .pidlddc-div-expiryYear': {
        width: '48%',
        marginLeft: '4%',
        paddingLeft: 0,
        display: 'inline-block',
        verticalAlign: 'top',
      },
      '& .pidlddc-input-expiryYear': {
        height: 32,
        width: '100%',
        border: '1px solid #bbbbbb',
      },
      '& .form-group': {
        marginTop: 5,
        marginBottom: 5,
      },
      '& .pidlddc-help-dialog-block': {
        display: 'none',
        position: 'absolute',
        left: 0,
        top: 0,
        height: '100%',
        width: '90%',
        overflow: 'visible',
        textAlign: 'center',
      },
      '& .pidlddc-help-dialog-content-block': {
        position: 'absolute',
        padding: '10px 15px 20px 15px',
        width: '90%',
        backgroundColor: palette.white,
        border: '1px solid #ccc',
        lineHeight: '20px',
        color: '#4f4f4f',
        zIndex: '1000',
        textTransform: 'none',
        textAlign: 'left',
        whiteSpace: 'normal',
        marginTop: '40%',
      },
      '& .pidlddc-help-dialog-close': {
        position: 'absolute',
        right: 10,
        top: 10,
        backgroundColor: 'transparent',
        cursor: 'pointer',
        padding: 0,
        border: 0,
        fontSize: 18,
        fontWeight: 'bold',
        color: '#666',
        outline: 'none',
      },
      '& .offline-pi-section': {
        '& .alert-spacing': {
          marginRight: '-7px',
        },
        '& .section-group-check-instruction, & .section-group-wire-instruction': {
          '& .page-header': {
            marginLeft: 15,
          },
          '& .section-body': {
            '& .col-xs-3': {
              width: '100%',
            },
          },
        },
      },
      '& .error': {
        fontSize: 12,
        color: '#A80000',
      },
      '& label .helpbutton': {
        fontWeight: 'normal',
        display: 'inline-block',
      },
      '& .auto-recharge-toggle': {
        marginBottom: 4,
      },
    },
    // TODO: Merge with Common once PIDLTemplates GA'ed
    pidlMigration: {
      '& .pidlddc-input-property': {
        float: 'none',
      },
      '& .pidlddc-div-cvv': {
        marginTop: 0,
      },
      '& .pidlddc-input-cvvToken': {
        verticalAlign: 'middle',
      },
      '& .pidlddc-help-imageLink': {
        verticalAlign: 'middle',
        marginTop: 0,
      },
      '& .pidlddc-input-addressLine1': {
        marginTop: 0,
      },
      '& .pidlddc-input-addressLine2 ': {
        marginTop: 0,
      },
      '& .pidlddc-input-addressCity': {
        marginTop: 0,
      },
      '& .pidlddc-input-addressState': {
        marginTop: 0,
      },
      '& .pidlddc-input-addressCounty': {
        marginTop: 0,
      },
      '& .pidlddc-input-addressProvince': {
        marginTop: 0,
      },
      '& .pidlddc-input-addressPostalCode': {
        marginTop: 0,
        marginBottom: 0,
        '& + .pidlddc-error': {
          margin: 0,
        },
      },
      '& .pidlddc-address-group': {
        marginTop: 0,
      },
      '& .pidlddc-div-cardNumber': {
        marginBottom: 0,
      },
      '& #pidlddc-div-acceptedVisaCardGroup,#pidlddc-div-acceptedAmexCardGroup,#pidlddc-div-acceptedMCCardGroup,#pidlddc-div-acceptedDiscoverCardGroup,#pidlddc-div-acceptedJCBCardGroup': {
        display: 'flex',
        alignItems: 'center',
        marginTop: 19,
        '& .pidlddc-static-text.pidlddc-text': {
          marginBottom: 0,
        },
      },
      '& .pidlddc-div-logo.pidlddc-logoBlock': {
        display: 'flex',
        '& img': {
          height: 20,
          marginLeft: 5,
        },
      },
      '& #pidlddc-div-cvvGroup': {
        position: 'relative',
      },
      '& .pidlddc-help-dialog-content-block': {
        marginTop: 0,
        // card help dialog height
        top: -240,
      },
      '& #pidlddc-text-paymentOptionSaveText': {
        marginTop: 10,
      },
      '& .pidlddc-text-group.pidlddc-privacy-text-group': {
        marginBottom: 0,
      },
      '& .pidlddc-label': {
        padding: '10px 0 5px',
      },
      '& .pidlddc-div-cardholderName': {
        marginTop: 4,
      },
      '& #pidlddc-text-paypalHeading': {
        display: 'none',
      },
      '& #pidlddc-text-paypalHeading ~ #pidlddc-text-microsoftPrivacyTextGroup': {
        paddingBottom: 4,
      },
      '& .pidlddc-error': {
        float: 'none',
        display: 'block',
      },
      '& #pidlddc-text-paypalInstructionTextGroup': {
        display: 'flex',
        backgroundColor: '#eff6fc',
        borderColor: '#eff6fc',
        color: '#323130',
        padding: '8px 32px 8px 8px',
        fontSize: 12,
        lineHeight: '16px',
        marginTop: '-5px',
      },
      '& #pidlddc-text-paypalInstructionTextGroup::before': {
        content: '"\\f12c"',
        fontFamily: '"BingAds UI Icons"',
        fontSize: 12,
        lineHeight: 1,
        color: '#605e5c',
        display: 'inline-block',
        fontWeight: 'normal',
        fontStyle: 'normal',
        speak: 'none',
        textDecoration: 'inherit',
        '-webkit-font-smoothing': 'antialiased',
        '-moz-osx-font-smoothing': 'grayscale',
        margin: '2px 8px 0 0',
      },
      '& #pidlddc-text-paymentSummaryText': {
        display: 'none',
      },
      '& #pidlddc-iFrame-ChallengeFrame': {
        border: 'none',
        marginTop: 10,
      },
    },
  });
};
